import { useTranslator } from '@eo-locale/preact';
import {
  asChoiceField,
  notEmpty,
  useField,
  useForm as useReactForm,
  validator,
} from '@shopify/react-form';
import { Geolocation } from 'types/common';
import { Review } from 'types/review';
import { NewReview } from 'widgets/ReviewsWidget/types/review';

import { useSettings } from 'components/SettingsProvider';

import { useReview } from './use-review';

const emailRegExp = new RegExp(
  "^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$",
  'i',
);

const isGreaterThanZero = validator<number>((input) => input > 0);
const isRequired = validator<boolean>((input) => input === true);
const isEmail = validator((value: string) => emailRegExp.test(value.trim()));

interface FormFields {
  rating: number;
  nickname: string;
  email: string;
  title: string;
  review: string;
  images?: Review['images'];
  pros?: string;
  cons?: string;
  privacy_policy: boolean;
}

export function useForm() {
  const { translate } = useTranslator();
  const {
    utils: { product, customer, locale, geolocation },
    settings,
  } = useSettings();
  const { addReview, isError, isSuccess, reset } = useReview();
  const {
    fields,
    submit,
    submitting,
    reset: resetFormFields,
  } = useReactForm({
    fields: {
      rating: useField({
        value: 0,
        validates: [
          isGreaterThanZero(
            translate('write_review_rating_required_error_message', {
              defaultMessage: 'Rating is required',
            }),
          ),
        ],
      }),
      nickname: useField({
        value: customer?.name ?? '',
        validates: [
          notEmpty(
            translate('write_review_nickname_required_error_message', {
              defaultMessage: 'Nickname is required',
            }),
          ),
        ],
      }),
      email: useField({
        value: customer?.email ?? '',
        validates: [
          isEmail(
            translate('write_review_email_not_valid_error_message', {
              defaultMessage: 'Email is not valid',
            }),
          ),
          notEmpty(
            translate('write_review_email_required_error_message', {
              defaultMessage: 'Email is required',
            }),
          ),
        ],
      }),
      title: useField({
        value: '',
        validates: !settings.reviews.review_form.hide_review_title
          ? [
              notEmpty(
                translate('write_review_summary_required_error_message', {
                  defaultMessage: 'Summary is required',
                }),
              ),
            ]
          : [],
      }),
      review: useField({
        value: '',
        validates: [
          notEmpty(
            translate('write_review_review_required_error_message', {
              defaultMessage: 'Review is required',
            }),
          ),
        ],
      }),
      images: useField<Review['images']>([]),
      pros: useField(''),
      cons: useField(''),
      privacy_policy: useField({
        value: false,
        validates: settings.reviews.review_form.show_privacy_policy
          ? [
              isRequired(
                translate('write_review_privacy_policy_error', {
                  defaultMessage: 'You must agree to the Privacy Policy',
                }),
              ),
            ]
          : [],
      }),
    },
    onSubmit: async (fields) => {
      if (product === null) {
        throw new Error('Webtex Reviews: Product is null.');
      }

      const customerId = customer ? customer.id : null;
      const request = beforeSave(
        fields,
        customerId,
        product.id,
        locale,
        geolocation,
      );

      try {
        await addReview(request);

        resetFormFields();

        return { status: 'success' };
      } catch (e) {
        return { status: 'fail', errors: [] };
      }
    },
  });

  return {
    fields: {
      ...fields,
      privacy_policy: asChoiceField(fields.privacy_policy),
    },
    submitError: isError,
    submitted: isSuccess,
    submit,
    reset,
    submitting,
  };
}

function beforeSave(
  {
    email,
    nickname,
    rating,
    review,
    privacy_policy,
    title,
    cons,
    pros,
    images,
  }: FormFields,
  customerId: number | null,
  productId: number,
  locale: string,
  geolocation: Geolocation,
): NewReview {
  return {
    email: email.toLowerCase(),
    nickname,
    rating,
    review,
    privacy_policy,
    title,
    cons: cons ? cons : null,
    pros: pros ? pros : null,
    customer_id: customerId,
    images: images ?? [],
    shopify_product_id: productId,
    locale,
    ...geolocation,
  };
}
